import { getToken, getUserIdFromToken, isTokenValid } from '../auth/tokenManager';

const WIZARD_CHECK_TTL_MS = 30000;

export const WIZARD_SETTLED_STORAGE_KEY = 'millet.wizardSettled';

function emptyWizardCheck() {
  return {
    token: null,
    userType: null,
    complete: null,
    checkedAt: 0,
  };
}

let recentWizardCheck = emptyWizardCheck();
let wizardCheckGeneration = 0;
let wizardCheckInFlight = null;
let wizardCheckInFlightKey = null;

function readWizardUserId() {
  if (typeof getUserIdFromToken !== 'function') return null;
  try {
    const userId = getUserIdFromToken(getToken());
    if (userId == null || userId === '') return null;
    return String(userId);
  } catch {
    return null;
  }
}

function readStoredWizardSettled() {
  try {
    const raw = localStorage.getItem(WIZARD_SETTLED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

function writeStoredWizardResult(userType, complete) {
  const userId = readWizardUserId();
  if (!userId || (userType !== 'brand' && userType !== 'influencer')) return;
  if (typeof complete !== 'boolean') return;

  try {
    const stored = readStoredWizardSettled();
    const previous = stored[userId];
    const userRecord = previous && typeof previous === 'object' ? previous : {};
    stored[userId] = {
      ...userRecord,
      [userType]: complete,
    };
    localStorage.setItem(WIZARD_SETTLED_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private mode or a full quota should not block navigation.
  }
}

function clearStoredWizardResults() {
  try {
    localStorage.removeItem(WIZARD_SETTLED_STORAGE_KEY);
  } catch {
    // Ignore storage failures while signing out.
  }
}

export function resetRecentWizardCheck() {
  wizardCheckGeneration += 1;
  recentWizardCheck = emptyWizardCheck();
  wizardCheckInFlight = null;
  wizardCheckInFlightKey = null;
  clearStoredWizardResults();
}

export function rememberWizardCheck(userType, complete) {
  const token = getToken();
  // An expired JWT still decodes to a user id. Do not settle onboarding from it.
  if (!token || !isTokenValid(token)) return;

  recentWizardCheck = {
    token,
    userType,
    complete,
    checkedAt: Date.now(),
  };
  writeStoredWizardResult(userType, complete);
}

export function rememberWizardCheckForGeneration(userType, complete, generation) {
  if (generation !== wizardCheckGeneration) return;
  rememberWizardCheck(userType, complete);
}

export function readRecentWizardCheck(userType) {
  const token = getToken();
  const now = Date.now();
  if (
    recentWizardCheck.userType !== userType
    || recentWizardCheck.complete == null
    || recentWizardCheck.token !== token
    || now - recentWizardCheck.checkedAt >= WIZARD_CHECK_TTL_MS
  ) {
    return null;
  }
  return { complete: recentWizardCheck.complete };
}

export function readSettledWizardCheck(userType) {
  if (userType !== 'brand' && userType !== 'influencer') return null;

  const token = getToken();
  if (
    recentWizardCheck.userType === userType
    && recentWizardCheck.complete != null
    && recentWizardCheck.token === token
  ) {
    return { complete: recentWizardCheck.complete };
  }

  const userId = readWizardUserId();
  if (!userId) return null;
  const complete = readStoredWizardSettled()?.[userId]?.[userType];
  if (typeof complete !== 'boolean') return null;
  return { complete };
}

export function takeWizardCheckFlight(userType, token) {
  const cached = readRecentWizardCheck(userType);
  if (cached) return { cachedComplete: cached.complete };

  const flightKey = `${userType}:${token || ''}`;
  if (wizardCheckInFlight && wizardCheckInFlightKey === flightKey) {
    return { promise: wizardCheckInFlight };
  }

  return { flightKey, generation: wizardCheckGeneration };
}

export function storeWizardCheckFlight(flightKey, promise) {
  wizardCheckInFlight = promise;
  wizardCheckInFlightKey = flightKey;
}

export function releaseWizardCheckFlight(promise) {
  if (wizardCheckInFlight === promise) {
    wizardCheckInFlight = null;
    wizardCheckInFlightKey = null;
  }
}

export function abandonWizardCheckFlight() {
  wizardCheckGeneration += 1;
  wizardCheckInFlight = null;
  wizardCheckInFlightKey = null;
}
